import { useState } from 'react';
import type { Task, Priority } from '../../../../mocks/crm';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const priorities: Priority[] = ['high', 'medium', 'low'];
const priorityColors: Record<Priority, string> = {
  high: '#DC2626',
  medium: '#D97706',
  low: '#059669',
};

const categories: Task['category'][] = ['follow_up', 'documentation', 'payment', 'booking', 'other'];
const categoryLabels: Record<Task['category'], string> = {
  follow_up: 'Follow-up',
  documentation: 'Documentation',
  payment: 'Payment',
  booking: 'Booking',
  other: 'Other',
};

interface AddTaskModalProps {
  onClose: () => void;
  onAdd: (task: Task) => void;
}

export default function AddTaskModal({ onClose, onAdd }: AddTaskModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('medium');
  const [category, setCategory] = useState<Task['category']>('follow_up');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [linkedLeadName, setLinkedLeadName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const newTask: Task = {
      id: `T${Date.now()}`,
      title: title.trim(),
      description: description.trim(),
      priority,
      status: 'pending',
      dueDate: dueDate || new Date().toISOString().slice(0, 10),
      dueTime: dueTime || '12:00 PM',
      assignedTo: 'Mohammed Khalid',
      assignedAvatar: 'MK',
      linkedLead: '',
      linkedLeadName: linkedLeadName.trim(),
      category,
      createdAt: new Date().toISOString().slice(0, 10),
    };
    onAdd(newTask);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800">Add New Task</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Task Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Follow up with Ahmed about honeymoon package"
              className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 placeholder-gray-400"
              style={{ '--tw-ring-color': brand } as React.CSSProperties}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional details about this task..."
              className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 resize-none focus:outline-none focus:ring-2 placeholder-gray-400"
              style={{ '--tw-ring-color': brand } as React.CSSProperties}
            />
          </div>

          {/* Priority + Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Priority</label>
              <div className="flex gap-2">
                {priorities.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className="flex-1 py-2 text-xs font-semibold rounded-xl capitalize cursor-pointer transition-all"
                    style={
                      priority === p
                        ? { backgroundColor: priorityColors[p], color: 'white' }
                        : { backgroundColor: '#F9FAFB', color: '#6B7280', border: '1px solid #E5E7EB' }
                    }
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as Task['category'])}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 bg-white focus:outline-none cursor-pointer"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{categoryLabels[c]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Due Date + Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none cursor-pointer"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Due Time</label>
              <input
                type="text"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                placeholder="e.g. 10:00 AM"
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none placeholder-gray-400"
              />
            </div>
          </div>

          {/* Linked Lead */}
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5 block">Linked Lead / Customer</label>
            <input
              type="text"
              value={linkedLeadName}
              onChange={(e) => setLinkedLeadName(e.target.value)}
              placeholder="e.g. Ahmed Al-Rashid"
              className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none placeholder-gray-400"
            />
          </div>

          {/* Submit */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 text-sm font-bold py-3 rounded-xl text-white cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: brand }}
            >
              Add Task
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
